import { useState, useCallback, useEffect, useRef } from 'react'
import cartDuck from 'components/cart/duck'
import cartItemsDuck from 'components/cart/cart-items/duck'
import voucherDuck from 'components/cart/cart-voucher-code/duck'
import { hasLocizeTranslation } from 'lib/utils/locize'
import segmentQuickAddToBagClicked from 'components/shared/quick-add-to-bag-modal/quick-add-to-bag-segment/quickAddToBagSegment'

export const addDiamondWithVoucher =
  ({ t, ...options }) =>
  async (dispatch, getState) => {
    const initialState = getState()
    if (
      initialState.cart.isFetching ||
      initialState.cartData.updatingVoucher ||
      initialState.cartData.removingItem
    )
      return
    const initialCart = initialState.cart.payload
    const initialVoucher = initialCart?.summary?.applied_voucher
    const confirmedCode =
      initialVoucher?.is_applied === true && initialVoucher.code
    dispatch(cartItemsDuck.creators.setUpdatingVoucher(true))
    try {
      const addedCart = await dispatch(cartDuck.creators.get(options))
      const currentState = getState()
      const cartID = initialCart?.metadata?.id_cart
      const addedMembership = addedCart?.items?.some(
        (item) =>
          item.type === 'diamond' &&
          String(item.id_product) === String(options.productID),
      )
      // The membership endpoint clears a confirmed voucher. Restore only that
      // cleared selection; a retained, invalidated or newer code always wins.
      const shouldRestoreVoucher =
        confirmedCode &&
        addedMembership &&
        addedCart.summary &&
        cartID != null &&
        String(addedCart?.metadata?.id_cart) === String(cartID) &&
        !addedCart?.summary?.applied_voucher?.code &&
        currentState.cart.payload === addedCart &&
        currentState.voucher === initialState.voucher &&
        !currentState.voucher.isFetching

      if (shouldRestoreVoucher) {
        dispatch(cartItemsDuck.creators.updateVoucherErrorMessage(null))
        let voucherResponse
        let refreshedCart
        try {
          voucherResponse = await dispatch(
            voucherDuck.creators.get({
              type: 'USE_VOUCHER',
              voucherCode: confirmedCode,
              ignoreCache: true,
            }),
          )
        } catch {
          voucherResponse = null
        }
        // Never calculate the stacked discount locally, including on failure.
        try {
          refreshedCart = await dispatch(
            cartDuck.creators.get({
              type: 'GET',
              body: options.body,
              ignoreCache: true,
              keepPayload: true,
            }),
          )
        } catch {
          refreshedCart = null
        }
        const restoredVoucher = refreshedCart?.summary?.applied_voucher
        if (
          restoredVoucher?.is_applied !== true ||
          restoredVoucher?.code !== confirmedCode
        ) {
          const serverMessage =
            refreshedCart?.summary?.notice?.voucher?.message ||
            voucherResponse?.error?.message ||
            voucherResponse?.error ||
            voucherResponse
          const retryMessage = hasLocizeTranslation(
            t,
            'CART_DIAMOND_VOUCHER_RETRY',
            initialState.internationalization.language?.split('-')[0] === 'th'
              ? 'ไม่สามารถยืนยันการใช้คูปองได้ กรุณาใช้คูปองอีกครั้งก่อนชำระเงิน'
              : 'We couldn’t confirm your voucher. Please apply it again before checkout.',
          )
          const error =
            typeof serverMessage === 'string'
              ? `${serverMessage} ${retryMessage}`
              : retryMessage
          dispatch(cartItemsDuck.creators.updateVoucherErrorMessage(error))
        }
      }
    } finally {
      dispatch(cartItemsDuck.creators.setUpdatingVoucher(false))
    }
  }

const useAddDiamondToCart = ({
  refType,
  addToCart,
  updateCart,
  id_product,
  id_product_attribute,
  applyStoreCredit,
  cartBusy = false,
  t,
}) => {
  const adding = useRef(false)
  const mounted = useRef(true)
  const [isAddingCart, setIsAddingCart] = useState(false)
  useEffect(
    () => () => {
      mounted.current = false
    },
    [],
  )
  const handleClickAddToCart = useCallback(async () => {
    if (adding.current || cartBusy) return
    adding.current = true
    setIsAddingCart(true)
    try {
      await addToCart({
        quantity: 1,
        type: 'AddToCart',
        ignoreCache: true,
        productID: id_product,
        productAttributeID: id_product_attribute,
        body: { is_store_credits_applied: applyStoreCredit },
        t,
      })
    } catch (error) {
      // eslint-disable-next-line
      console.error(error)
    } finally {
      adding.current = false
      if (mounted.current) setIsAddingCart(false)
      await updateCart({
        cartOpen: true,
        isStoreCreditUsed: applyStoreCredit,
      })
      segmentQuickAddToBagClicked({
        id_product,
        ref_type: refType,
      })
    }
  }, [
    refType,
    addToCart,
    updateCart,
    applyStoreCredit,
    id_product_attribute,
    id_product,
    cartBusy,
    t,
  ])

  return {
    handleClickAddToCart,
    isAddingCart: isAddingCart || cartBusy,
  }
}

export default useAddDiamondToCart
